//@ts-check
import {downloadCsvFromCdnUrl, downloadInventoryCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTableV2} from '@/hooks/useDataTableV2'
import {InventoryListColConfig} from '@/services/MockData/InventoryList/InventorytColConfig'
import {getInventory, getInventoryCsvFile} from '@/services/ProductsService/Fulfillment/inventory'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import gb from '@/utils/growthbookUtils'
import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */

/** @param {SortEntry | null} sorting @param {number} pageIndex @param {number} pageSize */
const buildQueryParams = (sorting, pageIndex, pageSize) => {
  const params = new URLSearchParams()
  params.append('sortBy', sorting?.column ?? 'productId')
  params.append('order', sorting?.direction ?? 'DESC')
  params.append('page', String(pageIndex))
  params.append('pageSize', String(pageSize))
  return params.toString()
}

/**
 * Data source hook for the v2 Inventory List table — wires `useDataTableV2` state
 * (pagination/sorting/filters) into the `getInventoryList` query, scoped to non-combo products.
 * @param {number} [defaultPageSize]
 * @param {string | null} [businessGroup] When set, adds a businessGroup filter to every request.
 */
export const useInventoryListTableV2 = (defaultPageSize = DEFAULT_TABLE_PAGE_SIZE, businessGroup = null) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const formInitialData = {
    promotionTemplateName: '',
    storeSelector: '',
    actions: '',
    numberOfDays: '',
    numberOfPoints: '',
    schedule: '',
    startDate: '',
    endDate: '',
    mediaFile: [],
  }
  const [formData, setFormData] = useState({...formInitialData})
  const debounceMS = gb.getFeatureValue(growthBookFeatureFlags.ProductSearchDebounceDelay, 2000)

  const {
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    handleResetAll,
    onTableReady,
    rowSelectionStateUpdater,
  } = useDataTableV2({defaultPageSize, debounceMs: debounceMS, columns: InventoryListColConfig})

  const searchFilters = useMemo(
    () => [
      ...activeFilters,
      ...(businessGroup ? [{field: 'businessGroup', value: businessGroup, type: 'string'}] : []),
    ],
    [activeFilters, businessGroup]
  )

  const filtersKey = useMemo(() => JSON.stringify(searchFilters), [searchFilters])
  const queryParams = buildQueryParams(sorting, pageIndex, pageSize)

  const {data, isFetching} = useQuery({
    queryKey: ['INVENTORY_LIST_V2', queryParams, filtersKey, searchFilters],
    queryFn: () => getInventory(searchFilters, queryParams),
    placeholderData: keepPreviousData,
  })

  const handleResetAllClick = useCallback(() => {
    handleResetAll()
  }, [handleResetAll])

  const resetFilterButtonOptions = useMemo(
    () => ({onFiltersReset: handleResetAllClick, text: 'Reset All'}),
    [handleResetAllClick]
  )

  const canDownload = selectedRows.length > 0 || isAllRowsSelected

  const onDownloadClick = useCallback(async () => {
    if (isDownloading || !canDownload) return
    setIsDownloading(true)
    try {
      const ids = selectedRows.map(item => ({
        productId: item.productId || '',
        warehouseId: item.warehouseId || '',
      }))
      const csvData = await getInventoryCsvFile({ids, filters: searchFilters}, queryParams)
      downloadCsvFromCdnUrl(csvData.cdnUrl, `InventoryList_${getDateWithTimeString()}`)
    } finally {
      setIsDownloading(false)
    }
  }, [isDownloading, canDownload, isAllRowsSelected, selectedRows, searchFilters, queryParams])

  const downloadInventoryTemplate = () => {
    const csvHeaders = ['Product ID', 'Location ID', 'Parent ID', 'GTIN', 'DOH', 'WHS OOS', 'Minimum Qty']
    const csvData = csvHeaders.join(',')
    const date = new Date()
    const timestamp = date.toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0]
    const fileName = `Inventory Template_${timestamp}`
    downloadInventoryCSVFiles(csvData, fileName)
  }

  return {
    columns: InventoryListColConfig,
    tableData: data?.data ?? [],
    isFetching,
    pageIndex: isFetching ? pageIndex : (data?.currentPage ?? pageIndex),
    pageSize: data?.pageSize ?? pageSize,
    totalRowsCount: data?.totalRecords ?? 0,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    selectedRows,
    isAllRowsSelected,
    resetKey,
    canDownload,
    isDownloading,
    onDownloadClick,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    resetFilterButtonOptions,
    onTableReady,
    rowSelectionStateUpdater,
    resetAll: handleResetAllClick,
    formData,
    setFormData,
    downloadInventoryTemplate,
  }
}
